import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Logo } from "@/components/Logo";
import { EntryShell, entryCardClass } from "@/components/layout/EntryShell";
import { TermsCheckbox } from "@/components/auth/TermsCheckbox";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useLanguage } from "@/hooks/useLanguage";
import { logAuthEvent } from "@/lib/audit";
import {
  acceptanceMetadata,
  needsTermsAcceptance,
  oauthSignupMethod,
  takePendingTerms,
} from "@/lib/auth/oauth";
import { readPlanIntent } from "@/lib/billing/planIntent";
import { track } from "@/lib/tracking/analytics";

const copy = {
  cs: {
    title: "Dokončete registraci",
    description:
      "Účet jste založili přes Google nebo Microsoft. Než začnete, potvrďte prosím obchodní podmínky.",
    continue: "Pokračovat",
    signOut: "Odhlásit se",
    required: "Pro pokračování je potřeba souhlas s obchodními podmínkami",
    failed: "Souhlas se nepodařilo uložit. Zkuste to prosím znovu.",
    accountFor: "Účet pro adresu",
    otherAccount: "Máte už účet s jiným e-mailem?",
    switchAccount: "Odhlaste se a přihlaste se jím",
  },
  en: {
    title: "Finish signing up",
    description:
      "You created your account with Google or Microsoft. Before you start, please accept the terms of service.",
    continue: "Continue",
    signOut: "Sign out",
    required: "Please accept the terms of service to continue",
    failed: "Could not save your acceptance. Please try again.",
    accountFor: "Account for",
    otherAccount: "Already have an account with another e-mail?",
    switchAccount: "Sign out and sign in with it",
  },
};

/**
 * Accounts created through Google or Microsoft carry no terms acceptance
 * (signInWithOAuth has no user metadata). An acceptance given on the register
 * tab before the redirect is written here without asking; otherwise the
 * person accepts here before the app (or the MCP consent) opens.
 */
export function TermsGate({ children }: { children: ReactNode }) {
  const { language } = useLanguage();
  const t = copy[language];
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();
  const needed = needsTermsAcceptance(user);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const pendingTried = useRef(false);
  const navigate = useNavigate();

  // Someone with an existing login who picked Google / Microsoft with another
  // address lands here on a brand-new account: let them back to the login form.
  const switchAccount = async () => {
    await supabase.auth.signOut();
    navigate(`/${language}/auth?tab=login`, { replace: true });
  };

  const save = async (data: Record<string, unknown>) => {
    setSaving(true);
    const { error: updateError } = await supabase.auth.updateUser({ data });
    setSaving(false);
    if (updateError) {
      setError(t.failed);
      return;
    }
    await logAuthEvent("TERMS_ACCEPTED", { user_id: user?.id, terms_version: data.terms_version });
    // A Google / Microsoft account's first terms acceptance is its sign-up; the
    // e-mail form fires signed_up itself (Auth.tsx).
    track("signed_up", { plan: readPlanIntent() ?? "none", method: oauthSignupMethod(user) });
    await queryClient.invalidateQueries({ queryKey: ["currentUser"] });
  };

  // Accepted on the register tab before leaving for the provider: apply it.
  useEffect(() => {
    if (!needed || pendingTried.current) return;
    pendingTried.current = true;
    const pending = takePendingTerms();
    if (pending) void save({ ...pending });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needed]);

  if (!needed) return <>{children}</>;

  return (
    <EntryShell>
      <Card className={entryCardClass}>
        <CardHeader className="text-center">
          <div className="mb-2 flex justify-center">
            <Logo size="lg" />
          </div>
          <CardTitle className="text-lg">{t.title}</CardTitle>
          <CardDescription>{t.description}</CardDescription>
          {user?.email && (
            <p className="text-sm text-muted-foreground" data-testid="gate-account-email">
              {t.accountFor} <span className="font-medium text-foreground">{user.email}</span>
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <TermsCheckbox
            id="gate-terms"
            language={language}
            checked={checked}
            onCheckedChange={setChecked}
            error={error}
          />
          <Button
            className="w-full"
            disabled={saving}
            onClick={() => {
              if (!checked) {
                setError(t.required);
                return;
              }
              setError(undefined);
              void save({ ...acceptanceMetadata(language) });
            }}
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t.continue}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => void supabase.auth.signOut()}>
            {t.signOut}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            {t.otherAccount}{" "}
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 font-medium"
              onClick={() => void switchAccount()}
            >
              {t.switchAccount}
            </Button>
          </p>
        </CardContent>
      </Card>
    </EntryShell>
  );
}
